import { useState, useEffect } from "react";
import api from "@/lib/api";

/**
 * ICT Definitions library — 13 core concepts, each with a hand-drawn SVG chart-style diagram.
 * Pure inline SVG so the modal is offline-ready, fast, and crisp at every zoom.
 */

const TERMS = [
  {
    id: "structure",
    title: "Structure",
    blurb: "The sequence of swing highs (HH/LH) and swing lows (HL/LL) that defines whether price is trending up, down, or ranging. Reading structure is the foundation of every ICT trade.",
    image: "structure",
  },
  {
    id: "bos",
    title: "Break of Structure (BOS)",
    blurb: "A confirmed close beyond the most recent swing high (bullish BOS) or swing low (bearish BOS) — signals the existing trend is continuing. The Internal Distribution Move (IDM) is the pullback that fuels it.",
    photo: "/ict-refs/bos.jpg",
  },
  {
    id: "mss",
    title: "Market Structure Shift (MSS)",
    blurb: "A reversal signal. Bullish MSS: in a downtrend, price closes above the most recent lower-high. Bearish MSS: in an uptrend, price closes below the most recent higher-low. First clue smart money has flipped direction.",
    photo: "/ict-refs/mss.png",
  },
  {
    id: "swings",
    title: "Swing Highs & Swing Lows",
    blurb: "A swing high is a candle with at least one lower-high to its left AND its right. A swing low is the mirror: lower-low on each side. They are the anchors price uses to seek liquidity.",
    image: "swings",
  },
  {
    id: "ext-int",
    title: "External vs. Internal Structure",
    blurb: "External structure is the high-timeframe leg (the macro range). Internal structure is the lower-timeframe pullback inside that leg. ICT traders use external for direction, internal for entries.",
    image: "extInt",
  },
  {
    id: "liquidity",
    title: "Liquidity",
    blurb: "Pools of resting buy- or sell-orders sitting above swing highs (BSL) or below swing lows (SSL). Smart Money targets these clusters to fill institutional size before reversing.",
    image: "liquidity",
  },
  {
    id: "low-res",
    title: "Low-Resistance Liquidity",
    blurb: "Liquidity that price can run with little opposition — usually equal highs / equal lows, recent session extremes, or stops above an untouched swing. Path-of-least-resistance for a sweep.",
    image: "lowRes",
  },
  {
    id: "high-res",
    title: "High-Resistance Liquidity",
    blurb: "Liquidity guarded by unfilled FVGs, breakers, or strong structural shifts. Price must absorb supply/demand on the way — these sweeps are slower and often fail.",
    image: "highRes",
  },
  {
    id: "fvg",
    title: "Fair Value Gap (FVG)",
    blurb: "Three-candle imbalance where the wicks of candle 1 and candle 3 don't overlap — institutional inefficiency that price typically returns to fill. Bullish FVG = demand zone, bearish FVG = supply zone.",
    photo: "/ict-refs/fvg-pair.webp",
  },
  {
    id: "ifvg",
    title: "Inverse FVG (IFVG)",
    blurb: "When price closes through an FVG and the polarity flips: a former bullish FVG becomes resistance (bearish iFVG), a former bearish FVG becomes support (bullish iFVG). High-probability re-entry zone.",
    photo: "/ict-refs/ifvg.webp",
  },
  {
    id: "risk",
    title: "Risk Management",
    blurb: "Risk a fixed % per trade (e.g., 1%). Size = $Risk ÷ stop-distance. Aim for ≥ 2R reward. Never average losers. The institutional edge survives drawdowns, not winning streaks.",
    image: "risk",
  },
  {
    id: "sessions",
    title: "Liquidity Sessions",
    blurb: "Asia (8pm–4am ET) sets the range. London (2am–5am ET) usually sweeps Asia's extremes. New York (8:30am–11am ET) sweeps London + delivers the daily expansion. Trade the sweep, not the range.",
    image: "sessions",
  },
  {
    id: "bias",
    title: "Bias — Up / Down / Consolidation",
    blurb: "Uptrend = HH + HL; Downtrend = LH + LL; Consolidation = price oscillates between two equal extremes with no new HH/LL. Your bias decides which liquidity pool is the target.",
    image: "bias",
  },
  {
    id: "smt",
    title: "SMT Divergence",
    blurb: "Smart Money Technique — two correlated assets (e.g., NQ vs. ES) disagree at a swing. One makes a new high, the other fails. The divergence reveals where institutional flow is hiding.",
    image: "smt",
  },
  {
    id: "pd",
    title: "Premium & Discount",
    blurb: "Split a range at 50% (the equilibrium). Above EQ = premium, sell zone. Below EQ = discount, buy zone. ICT entries always demand a discount in a bullish leg, premium in a bearish one.",
    image: "pd",
  },
  {
    id: "bs-ss",
    title: "Buyside & Sellside Liquidity",
    blurb: "Buyside liquidity (BSL) sits above highs (stops of shorts + breakout buys). Sellside liquidity (SSL) sits below lows (stops of longs + breakdown sells). Both are magnets for price.",
    photo: "/ict-refs/buyside-sellside-btc.png",
  },
];

export default function DefinitionsModal({ onClose }) {
  const [active, setActive] = useState(TERMS[0].id);
  const current = TERMS.find(t => t.id === active) || TERMS[0];

  // V2.6.8 — fire Student of the Market achievement when the modal first opens.
  useEffect(() => {
    let cancelled = false;
    api.post("/achievements/event", { event: "view_definitions" }).catch(() => {});
    return () => { cancelled = true; void cancelled; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-mono" data-testid="definitions-modal">
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-cyan-600/20 text-cyan-300 rounded-lg border border-cyan-500/30">
              <i className="fa-solid fa-book-open"/>
            </div>
            <div>
              <div className="text-white font-bold text-sm">ICT Concept Library</div>
              <div className="text-[10px] text-slate-500">{TERMS.length} core terminologies with visual references</div>
            </div>
          </div>
          <button data-testid="close-definitions" onClick={onClose} className="text-slate-500 hover:text-rose-400 text-xl">
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <div className="flex-1 grid grid-cols-[260px_1fr] overflow-hidden">
          <nav className="border-r border-slate-800 overflow-y-auto py-2">
            {TERMS.map(t => (
              <button key={t.id}
                data-testid={`def-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`w-full text-left px-4 py-2 text-xs border-l-2 transition-all ${
                  active === t.id
                    ? "bg-cyan-500/10 border-cyan-400 text-cyan-200 font-bold"
                    : "border-transparent text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
                }`}>
                {t.title}
              </button>
            ))}
          </nav>

          <section className="overflow-y-auto p-6 space-y-5">
            <div className="flex items-baseline justify-between">
              <h2 className="text-white text-2xl font-black tracking-tight">{current.title}</h2>
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">ICT Concept</span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed max-w-3xl">{current.blurb}</p>
            <div className="rounded-xl border border-slate-800 bg-[#090b0f] overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Chart Reference</div>
                <div className="text-[10px] text-slate-600">{current.photo ? "Real chart example" : "Stylised diagram"}</div>
              </div>
              <div className="p-4 bg-[#0a0d13] flex items-center justify-center">
                {current.photo
                  ? <img src={current.photo} alt={current.title} className="max-w-full max-h-[520px] object-contain rounded"/>
                  : <DefDiagram kind={current.image} />}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ---------- SVG diagrams ---------- */

const GRID = (
  <g stroke="rgba(255,255,255,0.04)">
    {Array.from({ length: 9 }).map((_, i) => (
      <line key={`v${i}`} x1={(i + 1) * 80} y1={0} x2={(i + 1) * 80} y2={360} />
    ))}
    {Array.from({ length: 6 }).map((_, i) => (
      <line key={`h${i}`} x1={0} y1={(i + 1) * 60} x2={800} y2={(i + 1) * 60} />
    ))}
  </g>
);

function Candle({ x, o, h, l, c, w = 10 }) {
  const up = c >= o;
  const fill = up ? "#10b981" : "#f43f5e";
  const top = Math.min(o, c);
  const bot = Math.max(o, c);
  return (
    <g>
      <line x1={x} x2={x} y1={h} y2={l} stroke={fill} strokeWidth="1.4" />
      <rect x={x - w / 2} y={top} width={w} height={Math.max(2, bot - top)} fill={fill} rx="1.5" />
    </g>
  );
}

function Frame({ children }) {
  return (
    <svg viewBox="0 0 800 360" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      <rect width="800" height="360" fill="#090b0f" />
      {GRID}
      {children}
    </svg>
  );
}

function DefDiagram({ kind }) {
  switch (kind) {
    case "structure": return <DiagStructure />;
    case "swings": return <DiagSwings />;
    case "extInt": return <DiagExtInt />;
    case "liquidity": return <DiagLiquidity />;
    case "lowRes": return <DiagLowRes />;
    case "highRes": return <DiagHighRes />;
    case "fvg": return <DiagFVG />;
    case "risk": return <DiagRisk />;
    case "sessions": return <DiagSessions />;
    case "bias": return <DiagBias />;
    case "smt": return <DiagSMT />;
    case "pd": return <DiagPD />;
    case "buySell": return <DiagBuySell />;
    default: return null;
  }
}

/* Each diagram returns a stylised chart with annotated labels */

function DiagStructure() {
  // HH HL HH HL HH → uptrend then break of structure
  const cs = [
    [60, 280, 230, 260, 250], [100, 250, 220, 240, 230],
    [140, 230, 200, 220, 210], [180, 220, 180, 200, 190],
    [220, 190, 240, 170, 220], [260, 220, 250, 200, 230],
    [300, 230, 270, 220, 250], [340, 250, 200, 190, 210],
    [380, 210, 180, 160, 170], [420, 170, 160, 130, 140],
    [460, 140, 200, 130, 190], [500, 190, 220, 180, 200],
    [540, 200, 260, 240, 250], [580, 250, 290, 270, 280],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      {/* HH/HL annotations */}
      <Tag x={180} y={150} label="HL" color="#10b981" />
      <Tag x={420} y={110} label="HH" color="#10b981" />
      <Tag x={500} y={235} label="HL" color="#10b981" />
      <text x={620} y={50} fill="#a7f3d0" fontSize="14" fontWeight="700" fontFamily="ui-monospace">UPTREND</text>
      <text x={620} y={70} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">HH + HL sequence</text>
    </Frame>
  );
}

function DiagSwings() {
  const cs = [
    [80, 240, 250, 220, 230], [120, 230, 210, 200, 215],
    [160, 215, 195, 180, 190], [200, 190, 220, 185, 215],
    [240, 215, 260, 210, 245], [280, 245, 290, 240, 275],
    [320, 275, 250, 240, 260], [360, 260, 230, 210, 220],
    [400, 220, 200, 175, 185], [440, 185, 200, 175, 195],
    [480, 195, 250, 190, 240], [520, 240, 280, 235, 270],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      <circle cx={280} cy={290} r="14" fill="none" stroke="#facc15" strokeWidth="2" />
      <text x={250} y={325} fill="#facc15" fontSize="11" fontWeight="700" fontFamily="ui-monospace">SWING HIGH</text>
      <circle cx={440} cy={175} r="14" fill="none" stroke="#38bdf8" strokeWidth="2" />
      <text x={410} y={160} fill="#38bdf8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">SWING LOW</text>
      <text x={600} y={50} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">3-bar pivot rule</text>
    </Frame>
  );
}

function DiagExtInt() {
  // Big macro swing high → swing low with internal pullback
  const cs = [
    [60, 290, 310, 280, 300], [100, 300, 280, 270, 285],
    [140, 285, 260, 245, 255], [180, 255, 230, 215, 225],
    [220, 225, 200, 180, 195], [260, 195, 175, 155, 165],
    [300, 165, 180, 155, 175], [340, 175, 200, 170, 195],
    [380, 195, 215, 185, 210], [420, 210, 230, 200, 220],
    [460, 220, 245, 215, 235], [500, 235, 220, 210, 215],
    [540, 215, 195, 180, 188], [580, 188, 175, 155, 162],
    [620, 162, 165, 130, 140],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      {/* External arrow */}
      <line x1={70} y1={300} x2={640} y2={140} stroke="#a78bfa" strokeWidth="2" strokeDasharray="5 4" />
      <text x={300} y={210} fill="#c4b5fd" fontSize="12" fontWeight="700" fontFamily="ui-monospace">EXTERNAL · macro leg</text>
      {/* Internal pullback band */}
      <rect x={260} y={155} width={240} height="90" fill="rgba(56,189,248,0.10)" stroke="rgba(56,189,248,0.45)" />
      <text x={290} y={150} fill="#38bdf8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">INTERNAL · pullback</text>
    </Frame>
  );
}

function DiagLiquidity() {
  const cs = [
    [80, 240, 220, 210, 215], [120, 215, 200, 190, 195],
    [160, 195, 180, 170, 175], [200, 175, 195, 175, 188],
    [240, 188, 175, 165, 170], [280, 170, 185, 168, 180],
    [320, 180, 165, 155, 158], [360, 158, 180, 158, 172],
    [400, 172, 200, 168, 195], [440, 195, 220, 190, 215],
    [480, 215, 235, 210, 230], [520, 230, 255, 225, 250],
    [560, 250, 165, 160, 168],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      {/* BSL above equal highs */}
      <line x1={150} y1={170} x2={550} y2={170} stroke="#38bdf8" strokeDasharray="4 3" strokeWidth="1.5"/>
      <text x={560} y={166} fill="#38bdf8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">BSL pool</text>
      {/* SSL below equal lows */}
      <line x1={150} y1={252} x2={550} y2={252} stroke="#f43f5e" strokeDasharray="4 3" strokeWidth="1.5"/>
      <text x={560} y={266} fill="#f43f5e" fontSize="11" fontWeight="700" fontFamily="ui-monospace">SSL pool</text>
      <text x={50} y={50} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">Resting orders above highs / below lows</text>
    </Frame>
  );
}

function DiagLowRes() {
  // Equal highs swept easily
  const cs = [
    [80, 240, 200, 195, 205], [120, 205, 195, 185, 192],
    [160, 192, 200, 185, 195], [200, 195, 200, 188, 198],
    [240, 198, 200, 190, 197], [280, 197, 200, 192, 199],
    [320, 199, 230, 198, 225], [360, 225, 250, 220, 245],
    [400, 245, 175, 160, 168],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      <line x1={60} y1={200} x2={350} y2={200} stroke="#facc15" strokeDasharray="3 3" strokeWidth="2"/>
      <text x={80} y={195} fill="#facc15" fontSize="11" fontWeight="700" fontFamily="ui-monospace">equal highs · low-res</text>
      <path d="M 365 200 q 35 -40 75 -45" stroke="#10b981" strokeWidth="2.5" fill="none" markerEnd="url(#arr)"/>
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 z" fill="#10b981"/>
        </marker>
      </defs>
      <text x={420} y={140} fill="#a7f3d0" fontSize="11" fontWeight="700" fontFamily="ui-monospace">easy sweep</text>
    </Frame>
  );
}

function DiagHighRes() {
  // Highs guarded by FVG
  const cs = [
    [80, 240, 200, 195, 205], [120, 205, 195, 185, 192],
    [160, 192, 200, 185, 195], [200, 195, 230, 190, 225],
    [240, 225, 250, 220, 240], [280, 240, 220, 210, 215],
    [320, 215, 205, 195, 200], [360, 200, 230, 195, 225],
    [400, 225, 215, 200, 208],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      <rect x={140} y={170} width={360} height="22" fill="rgba(249,115,22,0.25)" stroke="#f97316"/>
      <text x={520} y={186} fill="#f97316" fontSize="11" fontWeight="700" fontFamily="ui-monospace">unfilled FVG guard</text>
      <line x1={60} y1={160} x2={500} y2={160} stroke="#facc15" strokeDasharray="3 3" strokeWidth="2"/>
      <text x={80} y={155} fill="#facc15" fontSize="11" fontWeight="700" fontFamily="ui-monospace">target highs · high-res</text>
    </Frame>
  );
}

function DiagFVG() {
  // Three-candle bullish FVG, gap between candle 1 high and candle 3 low
  const cs = [
    [120, 250, 230, 215, 220], [180, 220, 200, 195, 205],
    [240, 205, 165, 158, 168],   // big candle 2 — leaves imbalance
    [300, 168, 175, 145, 150],
    [360, 150, 175, 145, 168], [420, 168, 195, 165, 188],
    [480, 188, 215, 185, 210], [540, 210, 230, 200, 225],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      {/* FVG box between candle1 high(215) and candle3 low(175) */}
      <rect x={120} y={175} width={420} height="40" fill="rgba(234,179,8,0.18)" stroke="#eab308" strokeDasharray="4 3"/>
      <text x={550} y={195} fill="#eab308" fontSize="11" fontWeight="700" fontFamily="ui-monospace">Bullish FVG</text>
      <text x={50} y={40} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">3-candle imbalance · price returns to fill</text>
      <text x={50} y={340} fill="#f97316" fontSize="11" fontWeight="700" fontFamily="ui-monospace">If closed-through → flips to IFVG resistance</text>
    </Frame>
  );
}

function DiagRisk() {
  return (
    <Frame>
      {/* Entry / SL / TP layout */}
      <line x1={60} y1={200} x2={740} y2={200} stroke="#94a3b8" strokeDasharray="4 3" strokeWidth="1.5"/>
      <text x={80} y={195} fill="#94a3b8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">ENTRY 100.00</text>
      <line x1={60} y1={260} x2={740} y2={260} stroke="#f43f5e" strokeWidth="2"/>
      <text x={80} y={278} fill="#f43f5e" fontSize="11" fontWeight="700" fontFamily="ui-monospace">SL 99.00 (-1R)</text>
      <line x1={60} y1={80} x2={740} y2={80} stroke="#10b981" strokeWidth="2"/>
      <text x={80} y={74} fill="#10b981" fontSize="11" fontWeight="700" fontFamily="ui-monospace">TP 102.50 (+2.5R)</text>
      {/* arrow */}
      <line x1={400} y1={260} x2={400} y2={90} stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" markerEnd="url(#riskArr)"/>
      <defs>
        <marker id="riskArr" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#38bdf8"/>
        </marker>
      </defs>
      <text x={420} y={170} fill="#38bdf8" fontSize="13" fontWeight="700" fontFamily="ui-monospace">R:R 2.5</text>
      <text x={420} y={190} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">size = $risk ÷ stop-distance</text>
      <rect x={520} y={245} width={200} height="40" fill="rgba(244,63,94,0.15)" stroke="#f43f5e"/>
      <text x={530} y={270} fill="#fda4af" fontSize="11" fontFamily="ui-monospace">Max loss: 1% of account</text>
    </Frame>
  );
}

function DiagSessions() {
  return (
    <Frame>
      {/* Asia box, London box, NY box */}
      <rect x={50} y={140} width={210} height="120" fill="rgba(59,130,246,0.10)" stroke="#3b82f6"/>
      <text x={130} y={130} fill="#93c5fd" fontSize="12" fontWeight="700" fontFamily="ui-monospace">ASIA</text>
      <line x1={50} y1={140} x2={260} y2={140} stroke="#3b82f6" strokeDasharray="3 2"/>
      <line x1={50} y1={260} x2={260} y2={260} stroke="#3b82f6" strokeDasharray="3 2"/>

      <rect x={280} y={110} width={210} height="180" fill="rgba(234,179,8,0.10)" stroke="#eab308"/>
      <text x={350} y={100} fill="#fde68a" fontSize="12" fontWeight="700" fontFamily="ui-monospace">LONDON</text>
      <text x={300} y={300} fill="#facc15" fontSize="10" fontFamily="ui-monospace">sweeps Asia high → expansion</text>

      <rect x={510} y={80} width={240} height="230" fill="rgba(244,114,182,0.10)" stroke="#f472b6"/>
      <text x={590} y={70} fill="#fbcfe8" fontSize="12" fontWeight="700" fontFamily="ui-monospace">NEW YORK</text>
      <text x={520} y={325} fill="#f472b6" fontSize="10" fontFamily="ui-monospace">sweeps London → daily delivery</text>

      {/* price line meandering through */}
      <polyline points="60,220 100,200 140,210 180,180 240,225 290,210 340,180 400,160 470,200 520,170 580,140 640,180 720,120"
        fill="none" stroke="#e5e7eb" strokeWidth="2"/>
    </Frame>
  );
}

function DiagBias() {
  // Three columns: uptrend, downtrend, consolidation
  return (
    <Frame>
      {/* Uptrend */}
      <polyline points="40,280 80,260 120,265 160,240 200,245 240,210 280,215"
        fill="none" stroke="#10b981" strokeWidth="2.5"/>
      <text x={100} y={310} fill="#a7f3d0" fontSize="12" fontWeight="700" fontFamily="ui-monospace">UPTREND</text>
      <text x={80} y={325} fill="#94a3b8" fontSize="10" fontFamily="ui-monospace">HH + HL</text>

      {/* Downtrend */}
      <polyline points="320,140 360,160 400,155 440,185 480,180 520,210 560,215"
        fill="none" stroke="#f43f5e" strokeWidth="2.5"/>
      <text x={380} y={310} fill="#fda4af" fontSize="12" fontWeight="700" fontFamily="ui-monospace">DOWNTREND</text>
      <text x={380} y={325} fill="#94a3b8" fontSize="10" fontFamily="ui-monospace">LH + LL</text>

      {/* Consolidation */}
      <polyline points="600,180 630,200 660,180 690,200 720,180 750,200"
        fill="none" stroke="#a78bfa" strokeWidth="2.5"/>
      <line x1={600} y1={170} x2={750} y2={170} stroke="#a78bfa" strokeDasharray="3 3"/>
      <line x1={600} y1={210} x2={750} y2={210} stroke="#a78bfa" strokeDasharray="3 3"/>
      <text x={600} y={310} fill="#c4b5fd" fontSize="12" fontWeight="700" fontFamily="ui-monospace">CONSOLIDATION</text>
      <text x={605} y={325} fill="#94a3b8" fontSize="10" fontFamily="ui-monospace">range bound</text>
    </Frame>
  );
}

function DiagSMT() {
  return (
    <Frame>
      {/* Top: NQ makes higher high */}
      <polyline points="60,220 120,200 180,180 240,150 300,120 360,140"
        fill="none" stroke="#10b981" strokeWidth="2.5"/>
      <text x={40} y={210} fill="#a7f3d0" fontSize="11" fontWeight="700" fontFamily="ui-monospace">NQ</text>
      <circle cx={300} cy={120} r="6" fill="#10b981"/>
      <text x={310} y={115} fill="#10b981" fontSize="10" fontWeight="700" fontFamily="ui-monospace">new HH ✓</text>

      <line x1={50} y1={180} x2={770} y2={180} stroke="rgba(148,163,184,0.15)"/>

      {/* Bottom: ES fails to confirm */}
      <polyline points="60,310 120,295 180,280 240,255 300,265 360,280"
        fill="none" stroke="#f472b6" strokeWidth="2.5"/>
      <text x={40} y={305} fill="#fbcfe8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">ES</text>
      <circle cx={300} cy={265} r="6" fill="#f43f5e"/>
      <text x={310} y={262} fill="#f43f5e" fontSize="10" fontWeight="700" fontFamily="ui-monospace">lower HH ✗</text>

      <text x={420} y={180} fill="#facc15" fontSize="14" fontWeight="700" fontFamily="ui-monospace">SMT DIVERGENCE</text>
      <text x={420} y={200} fill="#94a3b8" fontSize="11" fontFamily="ui-monospace">correlated assets disagree → reversal signal</text>
    </Frame>
  );
}

function DiagPD() {
  return (
    <Frame>
      {/* Range box */}
      <rect x={80} y={70} width={640} height="240" fill="none" stroke="#94a3b8" strokeDasharray="3 3"/>
      <line x1={80} y1={190} x2={720} y2={190} stroke="#a78bfa" strokeWidth="2"/>
      <text x={730} y={195} fill="#a78bfa" fontSize="11" fontWeight="700" fontFamily="ui-monospace">EQ 50%</text>
      <text x={730} y={75} fill="#f43f5e" fontSize="11" fontWeight="700" fontFamily="ui-monospace">HIGH</text>
      <text x={730} y={315} fill="#10b981" fontSize="11" fontWeight="700" fontFamily="ui-monospace">LOW</text>

      {/* Premium half */}
      <rect x={80} y={70} width={640} height="120" fill="rgba(244,63,94,0.08)"/>
      <text x={350} y={130} fill="#fda4af" fontSize="14" fontWeight="700" fontFamily="ui-monospace">PREMIUM · sell zone</text>

      {/* Discount half */}
      <rect x={80} y={190} width={640} height="120" fill="rgba(16,185,129,0.08)"/>
      <text x={340} y={260} fill="#a7f3d0" fontSize="14" fontWeight="700" fontFamily="ui-monospace">DISCOUNT · buy zone</text>
    </Frame>
  );
}

function DiagBuySell() {
  const cs = [
    [100, 240, 200, 195, 205], [140, 205, 195, 185, 192],
    [180, 192, 200, 185, 195], [220, 195, 200, 188, 198],
    [260, 198, 200, 190, 197], [300, 197, 230, 195, 220],
    [340, 220, 245, 215, 240], [380, 240, 175, 165, 170],
    [420, 170, 195, 165, 188], [460, 188, 200, 185, 195],
    [500, 195, 200, 190, 197], [540, 197, 200, 190, 196],
    [580, 196, 270, 195, 260],
  ];
  return (
    <Frame>
      {cs.map((c, i) => <Candle key={i} x={c[0]} o={c[1]} h={c[2]} l={c[3]} c={c[4]} />)}
      <line x1={60} y1={200} x2={600} y2={200} stroke="#38bdf8" strokeDasharray="3 3" strokeWidth="2"/>
      <text x={610} y={196} fill="#38bdf8" fontSize="11" fontWeight="700" fontFamily="ui-monospace">BUYSIDE (BSL)</text>
      <text x={610} y={210} fill="#94a3b8" fontSize="10" fontFamily="ui-monospace">stops of shorts</text>

      <line x1={60} y1={300} x2={600} y2={300} stroke="#f43f5e" strokeDasharray="3 3" strokeWidth="2"/>
      <text x={610} y={296} fill="#f43f5e" fontSize="11" fontWeight="700" fontFamily="ui-monospace">SELLSIDE (SSL)</text>
      <text x={610} y={310} fill="#94a3b8" fontSize="10" fontFamily="ui-monospace">stops of longs</text>
    </Frame>
  );
}

function Tag({ x, y, label, color }) {
  return (
    <g>
      <rect x={x - 14} y={y - 12} width="28" height="18" rx="3" fill={color} fillOpacity="0.18" stroke={color}/>
      <text x={x} y={y + 2} textAnchor="middle" fill={color} fontSize="11" fontWeight="700" fontFamily="ui-monospace">{label}</text>
    </g>
  );
}
